/**
 * Aritmética de datas históricas.
 *
 * Ano astronómico assinado: 1 aC = 0, 2 aC = -1, 1 dC = 1.
 * Não existe ano zero no calendário tradicional; a numeração astronómica
 * elimina o caso especial e torna a subtracção correcta em todo o intervalo.
 *
 * Módulo puro: sem zod, sem I/O.
 */

import type { Locale } from '../primitives/enums.js';

// ── conversão ────────────────────────────────────────────────

/** Ano aC (1-based) → ano astronómico. bce(1) === 0 */
export function bce(year: number): number {
  if (!Number.isInteger(year) || year < 1) {
    throw new RangeError(`ano aC tem de ser inteiro >= 1, recebido ${year}`);
  }
  return 1 - year;
}

/** Ano dC (1-based) → ano astronómico. ce(1) === 1 */
export function ce(year: number): number {
  if (!Number.isInteger(year) || year < 1) {
    throw new RangeError(`ano dC tem de ser inteiro >= 1, recebido ${year}`);
  }
  return year;
}

export interface DisplayYear {
  readonly year: number;
  readonly era: 'BCE' | 'CE';
}

/** Ano astronómico → ano de calendário com era. */
export function toDisplayYear(astronomical: number): DisplayYear {
  return astronomical <= 0
    ? { year: 1 - astronomical, era: 'BCE' }
    : { year: astronomical, era: 'CE' };
}

/** Número de anos decorridos entre dois anos astronómicos. */
export function yearsBetween(a: number, b: number): number {
  return Math.abs(b - a);
}

// ── séculos ──────────────────────────────────────────────────

/** Século dC N cobre [(N-1)*100+1, N*100]. 2º séc. dC → [101, 200]. */
export function centuryCE(n: number): { earliest: number; latest: number } {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`século inválido: ${n}`);
  return { earliest: (n - 1) * 100 + 1, latest: n * 100 };
}

/** Século aC N cobre de N*100 aC a ((N-1)*100+1) aC. 2º séc. aC → [-199, -100]. */
export function centuryBCE(n: number): { earliest: number; latest: number } {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`século inválido: ${n}`);
  return { earliest: bce(n * 100), latest: bce((n - 1) * 100 + 1) };
}

// ── parsing de atalho ────────────────────────────────────────

export interface ParsedSpan {
  readonly earliest: number;
  readonly latest: number;
  readonly precision: 'exact' | 'range' | 'circa' | 'century';
}

const ORDINAL = /^(\d+)(?:st|nd|rd|th|º|ª)?$/i;
const ERA_BCE = /^(bce?|a\.?\s?c\.?)$/i;
const ERA_CE = /^(ce|ad|d\.?\s?c\.?)$/i;

function parseOrdinal(token: string): number | null {
  const m = ORDINAL.exec(token.trim());
  if (!m?.[1]) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Parser de atalho para conteúdo semente e testes.
 *
 * Formas aceites:
 *   "30"              "30 CE"        "125 BCE"
 *   "26-36 CE"        "26–36 CE"     (hífen ou travessão)
 *   "c. 200 CE"       "circa 200 CE"
 *   "2nd c. CE"       "2c BCE"
 *   "3rd-2nd c. BCE"
 *
 * O YAML pode sempre dar earliest/latest explícitos; isto é conveniência.
 */
export function parseSpan(input: string): ParsedSpan {
  const raw = input.trim();
  if (raw.length === 0) throw new SyntaxError('data vazia');

  let s = raw.replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();

  let circa = false;
  const circaMatch = /^(c\.?|circa|ca\.?)\s+/i.exec(s);
  if (circaMatch) {
    circa = true;
    s = s.slice(circaMatch[0].length).trim();
  }

  let isCentury = false;
  const centuryMatch = /\bc(?:ent(?:ury|\.)?)?\.?\s*(?=(bce?|ce|ad|a\.?\s?c\.?|d\.?\s?c\.?)\b|$)/i.exec(s);
  if (centuryMatch) {
    isCentury = true;
    s = (s.slice(0, centuryMatch.index) + ' ' + s.slice(centuryMatch.index + centuryMatch[0].length)).trim();
  } else if (/^\d+(st|nd|rd|th)?c\b/i.test(s)) {
    isCentury = true;
    s = s.replace(/c\b/i, ' ').replace(/\s+/g, ' ').trim();
  }

  let era: 'BCE' | 'CE' = 'CE';
  const parts = s.split(' ');
  const last = parts[parts.length - 1];
  if (last !== undefined) {
    if (ERA_BCE.test(last)) {
      era = 'BCE';
      parts.pop();
    } else if (ERA_CE.test(last)) {
      era = 'CE';
      parts.pop();
    }
  }
  const body = parts.join(' ').trim();
  if (body.length === 0) throw new SyntaxError(`data sem número: "${raw}"`);

  const [fromTok, toTok] = body.includes('-')
    ? (body.split('-').map((t) => t.trim()) as [string, string | undefined])
    : ([body, undefined] as [string, string | undefined]);

  const from = parseOrdinal(fromTok);
  if (from === null) throw new SyntaxError(`número inválido em "${raw}": "${fromTok}"`);
  const to = toTok === undefined ? null : parseOrdinal(toTok);
  if (toTok !== undefined && to === null) {
    throw new SyntaxError(`número inválido em "${raw}": "${toTok}"`);
  }

  if (isCentury) {
    const span = (n: number) => (era === 'BCE' ? centuryBCE(n) : centuryCE(n));
    const a = span(from);
    const b = to === null ? a : span(to);
    return {
      earliest: Math.min(a.earliest, b.earliest),
      latest: Math.max(a.latest, b.latest),
      precision: 'century',
    };
  }

  const toAstro = (n: number) => (era === 'BCE' ? bce(n) : ce(n));
  const a = toAstro(from);
  const b = to === null ? a : toAstro(to);
  const earliest = Math.min(a, b);
  const latest = Math.max(a, b);

  return {
    earliest,
    latest,
    precision: circa ? 'circa' : earliest === latest ? 'exact' : 'range',
  };
}

// ── formatação ───────────────────────────────────────────────

interface EraLabels {
  readonly bce: string;
  readonly ce: string;
  readonly circa: string;
  readonly century: (roman: string, era: string) => string;
  readonly dash: string;
}

const LABELS: Record<Locale, EraLabels> = {
  en: {
    bce: 'BC',
    ce: 'AD',
    circa: 'c.',
    century: (r, e) => `${r} c. ${e}`,
    dash: '–',
  },
  es: {
    bce: 'a. C.',
    ce: 'd. C.',
    circa: 'c.',
    century: (r, e) => `s. ${r} ${e}`,
    dash: '–',
  },
  'pt-PT': {
    bce: 'a.C.',
    ce: 'd.C.',
    circa: 'c.',
    century: (r, e) => `séc. ${r} ${e}`,
    dash: '–',
  },
};

const ROMAN: readonly [number, string][] = [
  [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'],
  [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'],
  [5, 'V'], [4, 'IV'], [1, 'I'],
];

export function toRoman(n: number): string {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`romano inválido: ${n}`);
  let rest = n;
  let out = '';
  for (const [value, sym] of ROMAN) {
    while (rest >= value) {
      out += sym;
      rest -= value;
    }
  }
  return out;
}

export function formatYear(astronomical: number, locale: Locale = 'en'): string {
  const { year, era } = toDisplayYear(astronomical);
  const l = LABELS[locale];
  return `${year} ${era === 'BCE' ? l.bce : l.ce}`;
}

export interface FormattableSpan {
  readonly earliest: number;
  readonly latest: number;
  readonly precision?: string;
  readonly label?: string;
}

/**
 * Rende um intervalo para leitura.
 *
 * A `label` escrita à mão é respeitada **só na língua canónica**. Fora dela,
 * uma etiqueta autorada em inglês punha "26–36 AD" no meio de uma página em
 * espanhol — e as convenções de era são das mais visíveis de todas. Os
 * números e a `precision` chegam para derivar a forma certa em cada língua;
 * o que a etiqueta acrescenta é nuance de redacção, não informação.
 */
export function formatSpan(span: FormattableSpan, locale: Locale = 'en'): string {
  if (locale === 'en' && span.label !== undefined && span.label.length > 0) return span.label;
  const l = LABELS[locale];
  const prefix = span.precision === 'circa' ? `${l.circa} ` : '';

  if (span.precision === 'century') {
    const centuryOf = (astro: number): { n: number; era: 'BCE' | 'CE' } =>
      astro <= 0
        ? { n: Math.ceil((1 - astro) / 100), era: 'BCE' }
        : { n: Math.ceil(astro / 100), era: 'CE' };
    const a = centuryOf(span.earliest);
    const b = centuryOf(span.latest);
    const eraLabel = (e: 'BCE' | 'CE') => (e === 'BCE' ? l.bce : l.ce);
    if (a.n === b.n && a.era === b.era) {
      return prefix + l.century(toRoman(a.n), eraLabel(a.era));
    }
    return (
      prefix +
      `${l.century(toRoman(a.n), eraLabel(a.era))}${l.dash}${l.century(toRoman(b.n), eraLabel(b.era))}`
    );
  }

  if (span.earliest === span.latest) return prefix + formatYear(span.earliest, locale);

  const a = toDisplayYear(span.earliest);
  const b = toDisplayYear(span.latest);
  if (a.era === b.era) {
    const eraLabel = a.era === 'BCE' ? l.bce : l.ce;
    const lo = a.era === 'BCE' ? a.year : a.year;
    const hi = b.era === 'BCE' ? b.year : b.year;
    return prefix + `${lo}${l.dash}${hi} ${eraLabel}`;
  }
  return prefix + `${formatYear(span.earliest, locale)}${l.dash}${formatYear(span.latest, locale)}`;
}

// ── comparação e filtros ─────────────────────────────────────

export interface Span {
  readonly earliest: number;
  readonly latest: number;
}

/** Ordenação cronológica: por início, depois por fim. */
export function compareSpans(a: Span, b: Span): number {
  return a.earliest - b.earliest || a.latest - b.latest;
}

export function spanYears(s: Span): number {
  return s.latest - s.earliest;
}

export function midpoint(s: Span): number {
  return (s.earliest + s.latest) / 2;
}

export function containsYear(s: Span, year: number): boolean {
  return year >= s.earliest && year <= s.latest;
}

export function overlaps(a: Span, b: Span): boolean {
  return a.earliest <= b.latest && b.earliest <= a.latest;
}

/**
 * §112 "Before X" — leitura estrita: o item terminou de existir antes de `year`.
 * Um manuscrito datado 300–400 NÃO é certamente anterior a 325.
 */
export function certainlyBefore(s: Span, year: number): boolean {
  return s.latest <= year;
}

/**
 * §112 "Before X" — leitura permissiva: o item pode ser anterior a `year`.
 * Um manuscrito datado 300–400 pode ser anterior a 325.
 * É o comportamento por omissão do filtro, com indicador de incerteza na UI.
 */
export function possiblyBefore(s: Span, year: number): boolean {
  return s.earliest <= year;
}

/**
 * §111 "Earliest Evidence" — janela de N anos face a um evento âncora.
 * Usa `earliest` deliberadamente (§07): conservador, sem médias.
 */
export function withinYearsOf(s: Span, anchorYear: number, years: number): boolean {
  if (years < 0) throw new RangeError(`janela negativa: ${years}`);
  return Math.abs(s.earliest - anchorYear) <= years;
}

/** Distância conservadora de um intervalo a um ano âncora. */
export function distanceFrom(s: Span, anchorYear: number): number {
  if (containsYear(s, anchorYear)) return 0;
  return anchorYear < s.earliest ? s.earliest - anchorYear : anchorYear - s.latest;
}
